import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import WelcomeMessage from '../components/WelcomeMessage';
import UserDialog from '../components/UserDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import { API_BASE_URL } from '../config/api';
import { userProfilePayload } from '../utils/userPermissions';

const People = () => {
  const { people, roles, aircraft, addPerson, updatePerson, deletePerson, fetchUsers, fetchInstructors, fetchRoles, currentUser } = useApp();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dialogPos, setDialogPos] = useState({ x: 10, y: 10 });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePersonData, setDeletePersonData] = useState(null);
  const [userLookupId, setUserLookupId] = useState('');
  const [userLookupMessage, setUserLookupMessage] = useState('');
  const [userLookupMessageType, setUserLookupMessageType] = useState('');
  const [dialogUserData, setDialogUserData] = useState(null);
  const [hoveredProfileImage, setHoveredProfileImage] = useState(null);
  const [firstNameFilter, setFirstNameFilter] = useState('');
  const [lastNameFilter, setLastNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('desc');
  const [visibleColumns, setVisibleColumns] = useState({
    email: true,
    phone: true,
    address: true,
    city: true,
    state: true,
    zip: true,
    createdDate: true,
    lastLogin: true,
    googleCalSynced: true,
    lastFlightDate: true,
    status: true,
    role: true,
    aircraft: true
  });
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    title: '',
    about: '',
    comment: '',
    workingOn: '',
    status: 'active',
    roleId: '',
    orgRole: '',
    certifications: '',
    aircraftList: [],
    sortOrder: '',
    rate: '',
    payRate: '',
    referredByUserId: '',
    referralRate: '',
    isReferralRestricted: ''
  });
  const [selectedCertifications, setSelectedCertifications] = useState([]);
  const [activeTab, setActiveTab] = useState('basic');
  const [message, setMessage] = useState({ text: '', type: '' });
  const [weeklySchedule, setWeeklySchedule] = useState({
    sunday: { startTime: '', endTime: '' },
    monday: { startTime: '', endTime: '' },
    tuesday: { startTime: '', endTime: '' },
    wednesday: { startTime: '', endTime: '' },
    thursday: { startTime: '', endTime: '' },
    friday: { startTime: '', endTime: '' },
    saturday: { startTime: '', endTime: '' }
  });
  const [overrides, setOverrides] = useState([]);
  const [newOverride, setNewOverride] = useState({ startDate: '', startTime: '', endDate: '', endTime: '', overrideType: 'on' });
  const [editingOverride, setEditingOverride] = useState(null);
  const [personLessons, setPersonLessons] = useState([]);
  const [personUserInfo, setPersonUserInfo] = useState(null);
  const [workLessons, setWorkLessons] = useState([]);
  const [editingWorkLesson, setEditingWorkLesson] = useState(null);
  const [newWorkLesson, setNewWorkLesson] = useState({ clientId: '', date: '', startTime: '', clientName: '', dualGiven: '', groundInstruction: '', total: '' });
  const [workLessonMessage, setWorkLessonMessage] = useState('');
  const [activeClients, setActiveClients] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [expenseMessage, setExpenseMessage] = useState('');
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedReceiptImage, setSelectedReceiptImage] = useState('');
  const [earningsStatements, setEarningsStatements] = useState([]);
  const [editingEarningsId, setEditingEarningsId] = useState(null);
  const [earningsMessage, setEarningsMessage] = useState('');
  const [showEarningsDialog, setShowEarningsDialog] = useState(false);
  const [selectedEarnings, setSelectedEarnings] = useState(null);
  const [earningsWorkLessons, setEarningsWorkLessons] = useState([]);
  const [earningsExpenses, setEarningsExpenses] = useState([]);
  const [editingLessonId, setEditingLessonId] = useState(null);
  const [lessonMessage, setLessonMessage] = useState('');
  const [showDeleteWorkLessonModal, setShowDeleteWorkLessonModal] = useState(false);
  const [deleteWorkLessonId, setDeleteWorkLessonId] = useState(null);
  const [earningsDialogTab, setEarningsDialogTab] = useState('lessons');
  const [progressData, setProgressData] = useState([]);
  const [editingProgressId, setEditingProgressId] = useState(null);
  const [progressMessage, setProgressMessage] = useState('');
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [assignMessage, setAssignMessage] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [assignRole, setAssignRole] = useState('');
  const [assignOrgRole, setAssignOrgRole] = useState('');
  const [assignRate, setAssignRate] = useState('');
  const [assignPayRate, setAssignPayRate] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogData, setConfirmDialogData] = useState({ title: '', message: '', onConfirm: null });

  React.useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, [fetchUsers, fetchRoles]);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  const addAircraft = () => {
    setFormData({...formData, aircraftList: [...formData.aircraftList, { aircraftId: '', limitations: '' }]});
  };

  const removeAircraft = (index) => {
    setFormData({...formData, aircraftList: formData.aircraftList.filter((_, i) => i !== index)});
  };

  const updateAircraftList = (index, field, value) => {
    const updated = formData.aircraftList.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    );
    setFormData({...formData, aircraftList: updated});
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragOffset({ x: e.clientX - dialogPos.x, y: e.clientY - dialogPos.y });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setDialogPos({ x: e.clientX - dragOffset.x, y: e.clientY - dragOffset.y });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleDeletePerson = (person) => {
    if (!(currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser')) return;
    const displayName = `${person.firstName || person.first_name || ''} ${person.lastName || person.last_name || ''}`.trim();
    setDeletePersonData({ id: person.id, displayName });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!(currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') || !deletePersonData?.id) {
      setShowDeleteModal(false);
      setDeletePersonData(null);
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/user/${deletePersonData.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        }
      });
      if (response.ok) {
        deletePerson(deletePersonData.id);
        fetchUsers();
        fetchInstructors();
      }
    } catch (error) {
    }
    setShowDeleteModal(false);
    setDeletePersonData(null);
  };

  const isReadOnly = false;
  const canEditPilotAndSchedule = currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser';
  const canDeletePeople = currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser';
  const isPilotTabReadOnly = !canEditPilotAndSchedule;
  const isScheduleTabReadOnly = !canEditPilotAndSchedule;

  const formatDateOnly = (value) => {
    if (!value) return '';
    const source = String(value).trim();
    const datePartMatch = source.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (datePartMatch) {
      return `${datePartMatch[2]}/${datePartMatch[3]}/${datePartMatch[1]}`;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString();
  };

  const optionalColumns = [
    ['email', 'Email'],
    ['phone', 'Phone'],
    ['address', 'Address'],
    ['city', 'City'],
    ['state', 'State'],
    ['zip', 'ZIP'],
    ['createdDate', 'Created Date'],
    ['lastLogin', 'Last Login'],
    ['lastFlightDate', 'Last Flight Date'],
    ['googleCalSynced', 'Google Calendar Synced'],
    ['status', 'Status'],
    ['role', 'Role'],
    ['aircraft', 'Aircraft']
  ];
  const getFrozenColumnStyle = (columnIndex, isHeader = false, rowIndex = 0) => {
    const widths = [55, 60, 72, 140, 140];
    return {
      position: 'sticky',
      left: widths.slice(0, columnIndex).reduce((total, width) => total + width, 0),
      top: isHeader ? 0 : undefined,
      zIndex: isHeader ? 20 : 2,
      width: widths[columnIndex],
      minWidth: widths[columnIndex],
      backgroundColor: isHeader ? '#f0f0f0' : rowIndex % 2 === 0 ? 'white' : '#f8f9fa'
    };
  };

  const validatePassword = (password) => {
    const minLength = 10;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    if (password.length < minLength) {
      return 'Password must be at least 10 characters long';
    }
    if (!hasUpperCase) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!hasLowerCase) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!hasNumbers) {
      return 'Password must contain at least one number';
    }
    if (!hasSpecialChar) {
      return 'Password must contain at least one special character (!@#$%^&*(),.?":{}|<>)';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isReadOnly) return;
    setMessage({ text: '', type: '' });
    
    const currentFormData = e.formData || formData;
    
    if (currentFormData.password !== currentFormData.confirmPassword) {
      setMessage({ text: 'Error: Passwords do not match', type: 'error' });
      return { success: false, message: 'Passwords do not match' };
    }
    
    // Password validation (only for new users or when password is being changed)
    if (currentFormData.password && currentFormData.password.trim() !== '') {
      const passwordError = validatePassword(currentFormData.password);
      if (passwordError) {
        setMessage({ text: `Error: ${passwordError}`, type: 'error' });
        return { success: false, message: passwordError };
      }
    }
    
    try {
      const normalizedFormData = {
        ...currentFormData,
        referredByUserId: currentFormData.referredByUserId === '' || currentFormData.referredByUserId === null ? null : parseInt(currentFormData.referredByUserId, 10),
        referralRate: currentFormData.referralRate === '' || currentFormData.referralRate === null ? null : parseFloat(currentFormData.referralRate),
        isReferralRestricted: currentFormData.isReferralRestricted === '' || currentFormData.isReferralRestricted === null ? null : parseInt(currentFormData.isReferralRestricted, 10),
        sortOrder: currentFormData.sortOrder === '' || currentFormData.sortOrder === null ? null : parseInt(currentFormData.sortOrder, 10)
      };

      const payload = userProfilePayload(editingId ? {
        id: editingId,
        username: normalizedFormData.username,
        password: normalizedFormData.password,
        firstName: normalizedFormData.firstName,
        lastName: normalizedFormData.lastName,
        email: normalizedFormData.email,
        phone: normalizedFormData.phone,
        address1: normalizedFormData.address1,
        address2: normalizedFormData.address2,
        city: normalizedFormData.city,
        state: normalizedFormData.state,
        zip: normalizedFormData.zip,
        title: normalizedFormData.title,
        about: normalizedFormData.about,
        comment: normalizedFormData.comment,
        workingOn: normalizedFormData.workingOn,
        popupQuestionsOptOut: normalizedFormData.popupQuestionsOptOut,
        status: normalizedFormData.status,
        roleId: normalizedFormData.roleId,
        orgRole: normalizedFormData.orgRole,
        certifications: normalizedFormData.certifications,
        aircraftList: normalizedFormData.aircraftList,
        sortOrder: normalizedFormData.sortOrder,
        rate: normalizedFormData.rate,
        payRate: normalizedFormData.payRate,
        referredByUserId: normalizedFormData.referredByUserId,
        referralRate: normalizedFormData.referralRate,
        isReferralRestricted: normalizedFormData.isReferralRestricted
      } : normalizedFormData, currentUser?.user?.orgRole);

      let requestBody = JSON.stringify(payload);
      const requestHeaders = { 'Content-Type': 'application/json', 'session-id': currentUser?.sessionId };
      if (currentFormData.profileImage) {
        const multipartData = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value === undefined || value === null || key === 'profileImage' || key === 'profileImageUrl' || key === 'confirmPassword') return;
          const serializedValue = key === 'aircraftList'
            ? JSON.stringify(value)
            : ['isInstructor', 'isReferralRestricted', 'popupQuestionsOptOut'].includes(key)
              ? String(value === true || value === 1 || value === '1' || value === 'true')
              : value;
          multipartData.append(key, serializedValue);
        });
        multipartData.append('profileImage', currentFormData.profileImage);
        requestBody = multipartData;
        delete requestHeaders['Content-Type'];
      }
      const response = await fetch(`${API_BASE_URL}/user`, {
        method: 'POST',
        headers: requestHeaders,
        body: requestBody
      });
      
      if (response.ok) {
        const responseData = await response.json();
        const userId = editingId || responseData.id;
        
        // Save work schedule if user is an instructor and has schedule data
        let isInstructor = false;
        if (roles && !roles.error) {
          let rolesList = [];
          if (Array.isArray(roles)) {
            rolesList = roles;
          } else if (roles && typeof roles === 'object') {
            rolesList = Object.values(roles);
          }
          isInstructor = rolesList.find(r => r.id === parseInt(currentFormData.roleId))?.name === 'instructor';
        }
        if (isInstructor && userId) {
          const hasScheduleData = Object.values(weeklySchedule).some(day => day.startTime || day.endTime);
          if (hasScheduleData) {
            await saveInstructorScheduleForUser(userId);
          }
        }
        
        const successMsg = `Person record for ${currentFormData.firstName} ${currentFormData.lastName} ${editingId ? 'updated' : 'created'} successfully`;
        setMessage({ text: successMsg, type: 'success' });
        
        if (editingId) {
          updatePerson(editingId, currentFormData);
        } else {
          addPerson(currentFormData);
        }
        fetchUsers();
        
        setTimeout(() => {
          setFormData({ username: '', password: '', confirmPassword: '', firstName: '', lastName: '', email: '', phone: '', address1: '', address2: '', city: '', state: '', zip: '', title: '', about: '', comment: '', workingOn: '', status: 'active', roleId: '', orgRole: '', certifications: '', aircraftList: [], sortOrder: '', referredByUserId: '', referralRate: '', isReferralRestricted: '' });
          setSelectedCertifications([]);
          setWeeklySchedule({ sunday: { startTime: '', endTime: '' }, monday: { startTime: '', endTime: '' }, tuesday: { startTime: '', endTime: '' }, wednesday: { startTime: '', endTime: '' }, thursday: { startTime: '', endTime: '' }, friday: { startTime: '', endTime: '' }, saturday: { startTime: '', endTime: '' } });
          setActiveTab('basic');
          setEditingId(null);
          setMessage({ text: '', type: '' });
          setShowForm(false);
        }, 3000);
        return { success: true, userId };
      } else {
        const errorText = await response.text();
        let errorMessage = 'Failed to save user';
        try {
          const errorObj = JSON.parse(errorText);
          errorMessage = errorObj.error || errorText || 'Failed to save user';
        } catch {
          errorMessage = errorText || 'Failed to save user';
        }
        
        // Check if it's a 400 error indicating user already exists
        if (response.status === 400 && errorMessage.toLowerCase().includes('already exists')) {
          setConfirmDialogData({
            title: 'User Already Exists',
            message: `A user with the name "${currentFormData.firstName} ${currentFormData.lastName}" and email "${currentFormData.email}" already exists in the system. Would you like to assign this existing user to your organization instead?`,
            onConfirm: async () => {
              setShowConfirmDialog(false);
            // Search for the existing user
            try {
              const searchResponse = await fetch(`${API_BASE_URL}/user/search`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'session-id': currentUser?.sessionId
                },
                body: JSON.stringify({
                  searchTerm: currentFormData.email
                })
              });
              
              if (searchResponse.ok) {
                const searchResults = await searchResponse.json();
                const existingUser = searchResults.find(user => 
                  user.firstName === currentFormData.firstName && 
                  user.lastName === currentFormData.lastName && 
                  user.email === currentFormData.email
                );
                
                if (existingUser) {
                  // Pre-fill the assign dialog with the existing user's info
                  setSelectedUser(existingUser);
                  setAssignRole(currentFormData.roleId);
                  setAssignOrgRole(currentFormData.orgRole);
                  setAssignRate(currentFormData.rate);
                  setAssignPayRate(currentFormData.payRate);
                  setShowAssignDialog(true);
                  setShowForm(false);
                  return;
                }
              }
            } catch (searchError) {
              console.error('Error searching for existing user:', searchError);
            }
            }
          });
          setShowConfirmDialog(true);
          return { success: false, message: errorMessage };
        }
        
        setMessage({ text: `Error: ${errorMessage}`, type: 'error' });
        return { success: false, message: errorMessage };
      }
    } catch (error) {
      setMessage({ text: `Error: ${error.message}`, type: 'error' });
      return { success: false, message: error.message };
    }
  };

  const loadPersonLessons = async (userId) => {
    try {
      const userResponse = await fetch(`${API_BASE_URL}/user/${userId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (userResponse.ok) {
        const userData = await userResponse.json();
        setPersonUserInfo(userData);
      }
      
      const lessonsResponse = await fetch(`${API_BASE_URL}/lesson/user/${userId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (lessonsResponse.ok) {
        const lessonsData = await lessonsResponse.json();
        setPersonLessons(lessonsData);
      }
    } catch (error) {
      console.error('Error loading person lessons:', error);
    }
  };

  const loadActiveClients = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/clients/active`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setActiveClients(data);
      }
    } catch (error) {
      console.error('Error loading active clients:', error);
    }
  };

  const loadAllClients = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/clients/all`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAllClients(data);
      } else {
        console.error('Failed to load all clients:', await response.text());
      }
    } catch (error) {
      console.error('Error loading all clients:', error);
    }
  };

  const loadWorkLessons = async (instructorId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/work-lesson/instructor/${instructorId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      if (response.ok) {
        const data = await response.json();
        setWorkLessons(data);
      } else {
        const errorText = await response.text();
        console.error('Error response:', errorText);
      }
    } catch (error) {
      console.error('Error loading work lessons:', error);
    }
  };

  const getClientName = (clientId, fallbackName = '') => {
    if (!clientId) return fallbackName;
    const client = allClients.find(c => c.id == clientId);
    return client ? `${client.firstName} ${client.lastName}` : fallbackName;
  };

  const loadExpenses = async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/expense/user/${userId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setExpenses(data);
      }
    } catch (error) {
      console.error('Error loading expenses:', error);
    }
  };

  const saveExpense = async (expenseData) => {
    try {
      const formData = new FormData();
      
      if (expenseData.id && !expenseData.id.toString().startsWith('temp_')) {
        formData.append('id', expenseData.id);
      }
      formData.append('date', expenseData.date);
      formData.append('expense', expenseData.expense);
      formData.append('vendor', expenseData.vendor);
      formData.append('total', parseFloat(expenseData.total) || 0);
      formData.append('userId', editingId);
      if (expenseData.earningStatementId) {
        formData.append('earningStatementId', expenseData.earningStatementId);
      }
      if (expenseData.receiptFile) {
        formData.append('receiptImage', expenseData.receiptFile);
      }
      
      const response = await fetch(`${API_BASE_URL}/expense`, {
        method: 'POST',
        headers: {
          'session-id': currentUser?.sessionId
        },
        body: formData
      });
      
      if (response.ok) {
        setExpenseMessage('Expense saved successfully');
        loadExpenses(editingId);
        setEditingExpenseId(null);
        setTimeout(() => setExpenseMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setExpenseMessage(`Error: ${errorText}`);
        setTimeout(() => setExpenseMessage(''), 5000);
      }
    } catch (error) {
      setExpenseMessage(`Error: ${error.message}`);
      setTimeout(() => setExpenseMessage(''), 5000);
    }
  };

  const updateExpenseField = (expenseId, field, value) => {
    setExpenses(prev => prev.map(expense => 
      expense.id === expenseId ? { ...expense, [field]: value } : expense
    ));
  };

  const deleteExpense = async (expenseId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/expense/${expenseId}`, {
        method: 'DELETE',
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        loadExpenses(editingId);
      }
    } catch (error) {
      console.error('Error deleting expense:', error);
    }
  };

  const addNewExpense = () => {
    const tempId = `temp_${Date.now()}`;
    const newExpense = {
      id: tempId,
      date: new Date().toISOString().split('T')[0],
      expense: '',
      vendor: '',
      total: '',
      receiptImage: '',
      userId: editingId,
      earningStatementId: null
    };
    setExpenses([newExpense, ...expenses]);
    setEditingExpenseId(tempId);
  };

  const loadEarningsStatements = async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/earnings-statement/list/${userId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEarningsStatements(data);
      }
    } catch (error) {
      console.error('Error loading earnings statements:', error);
    }
  };

  const saveEarningsStatement = async (earningsData, customMessage = 'Earnings statement saved successfully') => {
    try {
      const payload = {
        startDate: earningsData.startDate,
        endDate: earningsData.endDate,
        userId: editingId,
        rentalHoursEarned: parseFloat(earningsData.rentalHoursEarned) || 0,
        rentalHoursUsed: parseFloat(earningsData.rentalHoursUsed) || 0
      };
      
      if (earningsData.id && !earningsData.id.toString().startsWith('temp_')) {
        payload.id = earningsData.id;
      }
      
      const response = await fetch(`${API_BASE_URL}/earnings-statement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const responseData = await response.json();
        setEarningsMessage(customMessage);
        
        // Update the row with response data
        setEarningsStatements(prev => prev.map(earnings => 
          earnings.id === earningsData.id ? { ...earnings, ...responseData } : earnings
        ));
        
        setEditingEarningsId(null);
        setTimeout(() => setEarningsMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setEarningsMessage(`Error: ${errorText}`);
        setTimeout(() => setEarningsMessage(''), 5000);
      }
    } catch (error) {
      setEarningsMessage(`Error: ${error.message}`);
      setTimeout(() => setEarningsMessage(''), 5000);
    }
  };

  const updateEarningsField = (earningsId, field, value) => {
    setEarningsStatements(prev => prev.map(earnings => 
      earnings.id === earningsId ? { ...earnings, [field]: value } : earnings
    ));
  };

  const deleteEarningsStatement = async (earningsId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/earnings-statement/${earningsId}`, {
        method: 'DELETE',
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        loadEarningsStatements(editingId);
      }
    } catch (error) {
      console.error('Error deleting earnings statement:', error);
    }
  };

  const addNewEarningsStatement = () => {
    const tempId = `temp_${Date.now()}`;
    const newEarnings = {
      id: tempId,
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      userId: editingId,
      rentalHoursEarned: '',
      rentalHoursUsed: ''
    };
    setEarningsStatements([newEarnings, ...earningsStatements]);
    setEditingEarningsId(tempId);
  };

  const loadEarningsDialogData = async (earnings) => {
    try {
      const response = await fetch(`${API_BASE_URL}/earnings-statement/items/${earnings.id}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      if (response.ok) {
        const data = await response.json();
        
        setEarningsWorkLessons(data.workLessons || []);
        setEarningsExpenses(data.expenses || []);
        setSelectedEarnings(prev => {
          const updated = {
            ...prev,
            rentalHoursEarned: data.rentalHoursEarned || prev.rentalHoursEarned || 0,
            rentalHoursUsed: data.rentalHoursUsed || prev.rentalHoursUsed || 0
          };
          return updated;
        });
      }
    } catch (error) {
      console.error('Error loading earnings dialog data:', error);
    }
  };

  const saveLesson = async (lessonData) => {
    try {
      const payload = {
        id: lessonData.id,
        userId: lessonData.userId,
        reservationId: lessonData.reservationId,
        date: lessonData.date,
        aircraftId: lessonData.aircraftId,
        instructorId: lessonData.instructorId,
        startTime: lessonData.startTime,
        endTime: lessonData.endTime,
        flightTime: parseFloat(lessonData.flightTime) || null,
        simulator: parseFloat(lessonData.simulator) || null,
        dualReceived: parseFloat(lessonData.dualReceived) || null,
        ground: parseFloat(lessonData.ground) || null,
        debrief: lessonData.debrief,
        nextFlight: lessonData.nextFlight,
        purchases: parseFloat(lessonData.purchases) || null,
        credit: parseFloat(lessonData.credit) || null,
        fee: parseFloat(lessonData.fee) || 0
      };
      
      const response = await fetch(`${API_BASE_URL}/lesson`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setLessonMessage('Lesson saved successfully');
        loadPersonLessons(editingId);
        setEditingLessonId(null);
        setTimeout(() => setLessonMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setLessonMessage(`Error: ${errorText}`);
        setTimeout(() => setLessonMessage(''), 5000);
      }
    } catch (error) {
      setLessonMessage(`Error: ${error.message}`);
      setTimeout(() => setLessonMessage(''), 5000);
    }
  };

  const updateLessonField = (lessonId, field, value) => {
    setPersonLessons(prev => prev.map(lesson => 
      lesson.id === lessonId ? { ...lesson, [field]: value } : lesson
    ));
  };

  const saveWorkLesson = async (lessonData) => {
    try {
      const payload = {
        id: lessonData.id,
        instructorId: editingId,
        clientId: lessonData.clientId || null,
        date: lessonData.date,
        startTime: lessonData.startTime,
        clientName: lessonData.clientName,
        dualGiven: parseFloat(lessonData.dualGiven) || 0,
        groundInstruction: parseFloat(lessonData.groundInstruction) || 0,
        total: parseFloat(lessonData.total) || 0
      };
      
      const response = await fetch(`${API_BASE_URL}/work-lesson`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setWorkLessonMessage('Lesson saved successfully');
        loadWorkLessons(editingId);
        setEditingWorkLesson(null);
        setTimeout(() => setWorkLessonMessage(''), 3000);
      } else {
        const errorText = await response.text();
        console.error('Save error:', errorText);
        setWorkLessonMessage(`Error: ${errorText}`);
        setTimeout(() => setWorkLessonMessage(''), 5000);
      }
    } catch (error) {
      console.error('Error saving work lesson:', error);
      setWorkLessonMessage(`Error: ${error.message}`);
      setTimeout(() => setWorkLessonMessage(''), 5000);
    }
  };

  const deleteWorkLesson = async (lessonId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/work-lesson/${lessonId}`, {
        method: 'DELETE',
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        loadWorkLessons(editingId);
      }
    } catch (error) {
      console.error('Error deleting work lesson:', error);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleEdit = (person) => {
    if (!person) return;

    setFormData({
      username: person.username || '',
      password: '',
      confirmPassword: '',
      firstName: person.firstName || person.first_name || '',
      lastName: person.lastName || person.last_name || '',
      email: person.email || '',
      phone: person.phone || '',
      address1: person.address1 || '',
      address2: person.address2 || '',
      city: person.city || '',
      state: person.state || '',
      zip: person.zip || '',
      title: person.title || '',
      about: person.about || '',
      comment: person.comment || '',
      workingOn: person.workingOn ?? person.pilotLevel ?? person.studentLevel ?? '',
      status: person.status || 'active',
      roleId: person.roleId || '',
      orgRole: person.orgRole || '',
      certifications: person.certifications || '',
      aircraftList: person.aircraftList || [],
      sortOrder: person.sortOrder ?? '',
      rate: person.rate || '',
      payRate: person.payRate || ''
    });
    setDialogUserData(person);
    setSelectedCertifications(person.certifications ? person.certifications.split(', ').filter(Boolean) : []);
    setActiveTab('basic');
    setEditingId(person.id);
    setMessage({ text: '', type: '' });
    
    // Reset work schedule to empty state
    setWeeklySchedule({
      sunday: { startTime: '', endTime: '' },
      monday: { startTime: '', endTime: '' },
      tuesday: { startTime: '', endTime: '' },
      wednesday: { startTime: '', endTime: '' },
      thursday: { startTime: '', endTime: '' },
      friday: { startTime: '', endTime: '' },
      saturday: { startTime: '', endTime: '' }
    });
    
    // Load earnings statements if person is staff
    if (person.orgRole === 'staff') {
      loadEarningsStatements(person.id);
    }
    
    setShowForm(true);
  };

  const openUserById = async () => {
    if (!(currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser')) {
      return;
    }

    const trimmedId = userLookupId.trim();
    const parsedId = Number(trimmedId);
    if (!trimmedId || !Number.isInteger(parsedId) || parsedId <= 0) {
      setUserLookupMessage('Enter a valid positive user ID');
      setUserLookupMessageType('error');
      setTimeout(() => {
        setUserLookupMessage('');
        setUserLookupMessageType('');
      }, 5000);
      return;
    }

    const existingPerson = (Array.isArray(people) ? people : []).find((person) => Number(person?.id) === parsedId);
    if (existingPerson) {
      handleEdit(existingPerson);
      setUserLookupMessage('Opened user details');
      setUserLookupMessageType('success');
      setTimeout(() => {
        setUserLookupMessage('');
        setUserLookupMessageType('');
      }, 3000);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/user/${parsedId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });

      if (response.ok) {
        const userData = await response.json();
        if (userData?.id) {
          handleEdit(userData);
          setUserLookupMessage('Opened user details');
          setUserLookupMessageType('success');
          setTimeout(() => {
            setUserLookupMessage('');
            setUserLookupMessageType('');
          }, 3000);
        } else {
          setUserLookupMessage('User not found');
          setUserLookupMessageType('error');
          setTimeout(() => {
            setUserLookupMessage('');
            setUserLookupMessageType('');
          }, 5000);
        }
      } else {
        const errorText = await response.text();
        setUserLookupMessage(`Error: ${errorText || 'Unable to find user'}`);
        setUserLookupMessageType('error');
        setTimeout(() => {
          setUserLookupMessage('');
          setUserLookupMessageType('');
        }, 5000);
      }
    } catch (error) {
      setUserLookupMessage(`Error: ${error.message}`);
      setUserLookupMessageType('error');
      setTimeout(() => {
        setUserLookupMessage('');
        setUserLookupMessageType('');
      }, 5000);
    }
  };

  const handleCancel = () => {
    setFormData({ username: '', password: '', confirmPassword: '', firstName: '', lastName: '', email: '', phone: '', address1: '', address2: '', city: '', state: '', zip: '', title: '', about: '', comment: '', workingOn: '', status: 'active', roleId: '', orgRole: '', certifications: '', aircraftList: [], sortOrder: '', rate: '', payRate: '' });
    setSelectedCertifications([]);
    setActiveTab('basic');
    setEditingId(null);
    setDialogUserData(null);
    setMessage({ text: '', type: '' });
    setPersonLessons([]);
    setPersonUserInfo(null);
    setWorkLessons([]);
    setExpenses([]);
    setEarningsStatements([]);
    setProgressData([]);
    setEditingProgressId(null);
    setProgressMessage('');
    setShowForm(false);
  };

  const handleCertificationChange = (certification) => {
    setSelectedCertifications(prev => 
      prev.includes(certification) 
        ? prev.filter(c => c !== certification)
        : [...prev, certification]
    );
  };

  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const militaryTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const ampm = hour < 12 ? 'AM' : 'PM';
        const displayTime = `${h12}:${minute.toString().padStart(2, '0')} ${ampm}`;
        options.push({ value: militaryTime, display: displayTime });
      }
    }
    return options;
  };

  const loadInstructorSchedule = async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/work-schedule/${userId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      if (response.ok) {
        const data = await response.json();
        const convertMilitaryToTime = (militaryTime) => {
          if (!militaryTime || militaryTime === 0) return '';
          const hours = Math.floor(militaryTime / 100);
          const minutes = militaryTime % 100;
          return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        };

        if (data) {
          const schedule = {
            sunday: { startTime: convertMilitaryToTime(data.sunStart), endTime: convertMilitaryToTime(data.sunEnd) },
            monday: { startTime: convertMilitaryToTime(data.monStart), endTime: convertMilitaryToTime(data.monEnd) },
            tuesday: { startTime: convertMilitaryToTime(data.tueStart), endTime: convertMilitaryToTime(data.tueEnd) },
            wednesday: { startTime: convertMilitaryToTime(data.wedStart), endTime: convertMilitaryToTime(data.wedEnd) },
            thursday: { startTime: convertMilitaryToTime(data.thuStart), endTime: convertMilitaryToTime(data.thuEnd) },
            friday: { startTime: convertMilitaryToTime(data.friStart), endTime: convertMilitaryToTime(data.friEnd) },
            saturday: { startTime: convertMilitaryToTime(data.satStart), endTime: convertMilitaryToTime(data.satEnd) }
          };
          setWeeklySchedule(schedule);
          // Load overrides from the same response - map API format to UI format
          const mappedOverrides = (data.scheduleOverrides || []).map(override => ({
            startDateTime: override.startDatetime,
            endDateTime: override.endDatetime,
            overrideType: override.overrideType
          }));
          setOverrides(mappedOverrides);
        }
      } else {
        setWeeklySchedule({
          sunday: { startTime: '', endTime: '' },
          monday: { startTime: '', endTime: '' },
          tuesday: { startTime: '', endTime: '' },
          wednesday: { startTime: '', endTime: '' },
          thursday: { startTime: '', endTime: '' },
          friday: { startTime: '', endTime: '' },
          saturday: { startTime: '', endTime: '' }
        });
        setOverrides([]);
      }
    } catch (error) {
      setWeeklySchedule({
        sunday: { startTime: '', endTime: '' },
        monday: { startTime: '', endTime: '' },
        tuesday: { startTime: '', endTime: '' },
        wednesday: { startTime: '', endTime: '' },
        thursday: { startTime: '', endTime: '' },
        friday: { startTime: '', endTime: '' },
        saturday: { startTime: '', endTime: '' }
      });
      setOverrides([]);
    }
  };



  const saveInstructorSchedule = async () => {
    try {
      const convertTimeToMilitary = (timeStr) => {
        if (!timeStr) return 0;
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours * 100 + minutes;
      };

      // Save weekly schedule with overrides
      const payload = {
        userId: editingId,
        sunStart: convertTimeToMilitary(weeklySchedule.sunday.startTime),
        sunEnd: convertTimeToMilitary(weeklySchedule.sunday.endTime),
        monStart: convertTimeToMilitary(weeklySchedule.monday.startTime),
        monEnd: convertTimeToMilitary(weeklySchedule.monday.endTime),
        tueStart: convertTimeToMilitary(weeklySchedule.tuesday.startTime),
        tueEnd: convertTimeToMilitary(weeklySchedule.tuesday.endTime),
        wedStart: convertTimeToMilitary(weeklySchedule.wednesday.startTime),
        wedEnd: convertTimeToMilitary(weeklySchedule.wednesday.endTime),
        thuStart: convertTimeToMilitary(weeklySchedule.thursday.startTime),
        thuEnd: convertTimeToMilitary(weeklySchedule.thursday.endTime),
        friStart: convertTimeToMilitary(weeklySchedule.friday.startTime),
        friEnd: convertTimeToMilitary(weeklySchedule.friday.endTime),
        satStart: convertTimeToMilitary(weeklySchedule.saturday.startTime),
        satEnd: convertTimeToMilitary(weeklySchedule.saturday.endTime),
        scheduleOverrideList: overrides
      };

      const response = await fetch(`${API_BASE_URL}/work-schedule`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setMessage({ text: 'Work schedule and overrides saved successfully', type: 'success' });
      } else {
        const errorText = await response.text();
        let errorMessage = 'Failed to save work schedule';
        try {
          const errorObj = JSON.parse(errorText);
          errorMessage = errorObj.error || errorText || 'Failed to save work schedule';
        } catch {
          errorMessage = errorText || 'Failed to save work schedule';
        }
        setMessage({ text: `Error: ${errorMessage}`, type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'Error: Error saving work schedule', type: 'error' });
    }
  };

  const addOverride = () => {
    if (!newOverride.startDate || !newOverride.startTime || !newOverride.endDate || !newOverride.endTime) return;
    
    const startDateTime = `${newOverride.startDate}T${newOverride.startTime}:00`;
    const endDateTime = `${newOverride.endDate}T${newOverride.endTime}:00`;
    
    setOverrides(prev => [...prev, {
      startDateTime,
      endDateTime,
      overrideType: newOverride.overrideType
    }]);
    setNewOverride({ startDate: '', startTime: '', endDate: '', endTime: '', overrideType: 'on' });
  };

  const removeOverride = (index) => {
    setOverrides(overrides.filter((_, i) => i !== index));
  };

  const updateOverride = (index, field, value) => {
    const updated = overrides.map((override, i) => {
      if (i === index) {
        if (field === 'startDate' || field === 'startTime') {
          const startDate = field === 'startDate' ? value : override.startDateTime.split('T')[0];
          const startTime = field === 'startTime' ? value : override.startDateTime.split('T')[1].slice(0, 5);
          return { ...override, startDateTime: `${startDate}T${startTime}:00` };
        } else if (field === 'endDate' || field === 'endTime') {
          const endDate = field === 'endDate' ? value : override.endDateTime.split('T')[0];
          const endTime = field === 'endTime' ? value : override.endDateTime.split('T')[1].slice(0, 5);
          return { ...override, endDateTime: `${endDate}T${endTime}:00` };
        } else {
          return { ...override, [field]: value };
        }
      }
      return override;
    });
    setOverrides(updated);
  };

  const loadProgressData = async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/requirement/user/${userId}`, {
        method: 'GET',
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProgressData(data);
      } else {
        setProgressData([]);
      }
    } catch (error) {
      setProgressData([]);
    }
  };

  const updateProgressField = (itemId, field, value) => {
    setProgressData(prev => prev.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    ));
  };

  const saveProgressItem = async (item) => {
    try {
      const payload = [{
        userId: parseInt(editingId),
        requirementId: item.id,
        status: item.status,
        level: item.level
      }];
      
      const response = await fetch(`${API_BASE_URL}/requirement/user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setProgressMessage('Progress updated successfully');
        setEditingProgressId(null);
        setTimeout(() => setProgressMessage(''), 3000);
      } else {
        setProgressMessage('Error: Failed to update progress');
        setTimeout(() => setProgressMessage(''), 5000);
      }
    } catch (error) {
      setProgressMessage(`Error: ${error.message}`);
      setTimeout(() => setProgressMessage(''), 5000);
    }
  };

  const saveInstructorScheduleForUser = async (userId) => {
    try {
      const convertTimeToMilitary = (timeStr) => {
        if (!timeStr) return 0;
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours * 100 + minutes;
      };

      const payload = {
        userId: userId,
        sunStart: convertTimeToMilitary(weeklySchedule.sunday.startTime),
        sunEnd: convertTimeToMilitary(weeklySchedule.sunday.endTime),
        monStart: convertTimeToMilitary(weeklySchedule.monday.startTime),
        monEnd: convertTimeToMilitary(weeklySchedule.monday.endTime),
        tueStart: convertTimeToMilitary(weeklySchedule.tuesday.startTime),
        tueEnd: convertTimeToMilitary(weeklySchedule.tuesday.endTime),
        wedStart: convertTimeToMilitary(weeklySchedule.wednesday.startTime),
        wedEnd: convertTimeToMilitary(weeklySchedule.wednesday.endTime),
        thuStart: convertTimeToMilitary(weeklySchedule.thursday.startTime),
        thuEnd: convertTimeToMilitary(weeklySchedule.thursday.endTime),
        friStart: convertTimeToMilitary(weeklySchedule.friday.startTime),
        friEnd: convertTimeToMilitary(weeklySchedule.friday.endTime),
        satStart: convertTimeToMilitary(weeklySchedule.saturday.startTime),
        satEnd: convertTimeToMilitary(weeklySchedule.saturday.endTime)
      };

      await fetch(`${API_BASE_URL}/work-schedule`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
    } catch (error) {
    }
  };

  const searchUsers = async () => {
    if (searchQuery.trim().length < 3) {
      setSearchResults([]);
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/user/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify({
          searchTerm: searchQuery.trim()
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
      } else {
        const errorText = await response.text();
        console.error('Search error response:', errorText);
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    }
  };

  const assignUserToOrganization = async () => {
    if (!selectedUser || !assignRole || !assignOrgRole) {
      setAssignMessage('Please select a user and fill in all fields');
      return;
    }

    try {
      const payload = {
        userId: selectedUser.id,
        role: assignRole,
        orgRole: assignOrgRole
      };
      
      // Add rate fields if instructor role is selected
      const isInstructor = (() => {
        if (roles && roles.error) {
          return assignRole === '3';
        }
        let rolesList = [];
        if (Array.isArray(roles)) {
          rolesList = roles;
        } else if (roles && typeof roles === 'object') {
          rolesList = Object.values(roles);
        }
        const selectedRole = rolesList.find(r => r.id === parseInt(assignRole));
        return selectedRole?.name === 'instructor';
      })();
      
      if (isInstructor) {
        payload.rate = assignRate;
        payload.payRate = assignPayRate;
      }
      
      const response = await fetch(`${API_BASE_URL}/user/organization/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setAssignMessage(`Successfully assigned ${selectedUser.firstName} ${selectedUser.lastName} to organization`);
        setSelectedUser(null);
        setAssignRole('');
        setAssignOrgRole('');
        setAssignRate('');
        setAssignPayRate('');
        setSearchQuery('');
        setSearchResults([]);
        
        // Refresh the people list to show the newly assigned user
        fetchUsers();
        
        setTimeout(() => {
          setAssignMessage('');
          setShowAssignDialog(false);
        }, 3000);
      } else {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        setAssignMessage(`Error: ${errorText}`);
      }
    } catch (error) {
      console.error('Catch error:', error);
      setAssignMessage(`Error: ${error.message}`);
    }
  };

  return (
    <div>
      <WelcomeMessage />
      <div style={{ padding: '2rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', marginBottom: '2rem', columnGap: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
          <h2 style={{ margin: 0 }}>People</h2>
          <details style={{ position: 'relative', display: 'inline-block', flexShrink: 0, marginLeft: '1in' }}>
            <summary style={{ cursor: 'pointer', padding: '0.4rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px' }}>Show / hide columns</summary>
            <div style={{ position: 'absolute', zIndex: 30, top: '100%', left: 0, display: 'grid', gridTemplateColumns: 'repeat(2, max-content)', gap: '0.35rem 1rem', padding: '0.75rem', backgroundColor: 'white', border: '1px solid #ccc', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
              {optionalColumns.map(([key, label]) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', whiteSpace: 'nowrap' }}>
                  <input
                    type="checkbox"
                    checked={visibleColumns[key]}
                    onChange={() => setVisibleColumns(columns => ({ ...columns, [key]: !columns[key] }))}
                  />
                  {label}
                </label>
              ))}
            </div>
          </details>
        </div>
        {(currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') && (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', justifyContent: 'center' }}>
            <label style={{ fontWeight: '600' }}>Open User By ID:</label>
            <input
              type="text"
              value={userLookupId}
              onChange={(e) => setUserLookupId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  openUserById();
                }
              }}
              placeholder="Enter user ID"
              style={{ padding: '0.45rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '180px' }}
            />
            <button
              onClick={openUserById}
              style={{ backgroundColor: '#007bff', color: 'white', border: 'none', padding: '0.45rem 0.8rem', borderRadius: '4px', cursor: 'pointer' }}
            >
              Open
            </button>
          </div>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button onClick={() => fetchUsers()} style={{ padding: '0.5rem', borderRadius: '4px', cursor: 'pointer' }} title="Refresh"><span style={{ fontSize: '1.5rem' }}>🔄</span></button>
          <button onClick={() => setShowForm(true)}>Add Person</button>
          {(currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser' || currentUser?.user?.orgRole === 'staff') && (
            <button onClick={() => setShowAssignDialog(true)} style={{ backgroundColor: '#28a745', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}>Assign Person</button>
          )}
        </div>
      </div>

      {userLookupMessage && (currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.5rem',
          borderRadius: '4px',
          backgroundColor: userLookupMessageType === 'success' ? '#d4edda' : '#f8d7da',
          color: userLookupMessageType === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${userLookupMessageType === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {userLookupMessage}
        </div>
      )}

      <style>{optionalColumns.map(([key], index) => (
        visibleColumns[key] ? '' : `.people-table th:nth-child(${index + 6}), .people-table td:nth-child(${index + 6}) { display: none; }`
      )).join('')}</style>
      <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
        <table className="people-table" style={{ width: 'max-content', minWidth: '100%', borderCollapse: 'collapse' }}>
        <colgroup>
          {[55, 60, 72, 140, 140].map((width, index) => <col key={index} style={{ width, minWidth: width }} />)}
          {optionalColumns.map(([key]) => <col key={key} />)}
        </colgroup>
        <thead>
          <tr style={{ backgroundColor: '#f0f0f0', position: 'sticky', top: 0, zIndex: 10 }}>
            <th style={{ ...getFrozenColumnStyle(0, true), padding: '0.5rem', border: '1px solid #ccc', textAlign: 'center', verticalAlign: 'top' }}>#</th>
            <th style={{ ...getFrozenColumnStyle(1, true), padding: '0.5rem', border: '1px solid #ccc', textAlign: 'center', verticalAlign: 'top' }}>Del</th>
            <th style={{ ...getFrozenColumnStyle(2, true), padding: '0.5rem', border: '1px solid #ccc', textAlign: 'center', verticalAlign: 'top' }}>Image</th>
            <th style={{ ...getFrozenColumnStyle(3, true), padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', verticalAlign: 'top' }}>
              <div>First Name</div>
              <input 
                type="text" 
                placeholder="Filter..." 
                value={firstNameFilter} 
                onChange={(e) => setFirstNameFilter(e.target.value)}
                style={{ width: '100%', padding: '0.25rem', marginTop: '0.25rem', fontSize: '0.8rem', border: '1px solid #ddd', borderRadius: '3px' }}
              />
            </th>
            <th style={{ ...getFrozenColumnStyle(4, true), padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', verticalAlign: 'top' }}>
              <div>Last Name</div>
              <input 
                type="text" 
                placeholder="Filter..." 
                value={lastNameFilter} 
                onChange={(e) => setLastNameFilter(e.target.value)}
                style={{ width: '100%', padding: '0.25rem', marginTop: '0.25rem', fontSize: '0.8rem', border: '1px solid #ddd', borderRadius: '3px' }}
              />
            </th>
            <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', verticalAlign: 'top' }}>Email</th>
            <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', verticalAlign: 'top' }}>Phone</th>
            <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', verticalAlign: 'top' }}>Address</th>
            <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', verticalAlign: 'top' }}>City</th>
            <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', verticalAlign: 'top' }}>State</th>
            <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', verticalAlign: 'top' }}>ZIP</th>
            <th
              title="Click to sort"
              style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', verticalAlign: 'top', cursor: 'pointer', userSelect: 'none', color: sortColumn === 'createdDate' ? '#0d6efd' : 'inherit' }}
              onClick={() => handleSort('createdDate')}
            >
              Created Date <span style={{ opacity: sortColumn === 'createdDate' ? 1 : 0.4 }}>{sortColumn === 'createdDate' ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅'}</span>
            </th>
            <th
              title="Click to sort"
              style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', verticalAlign: 'top', cursor: 'pointer', userSelect: 'none', color: sortColumn === 'lastLogin' ? '#0d6efd' : 'inherit' }}
              onClick={() => handleSort('lastLogin')}
            >
              Last Login <span style={{ opacity: sortColumn === 'lastLogin' ? 1 : 0.4 }}>{sortColumn === 'lastLogin' ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅'}</span>
            </th>
            <th
              title="Click to sort"
              style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', verticalAlign: 'top', cursor: 'pointer', userSelect: 'none', color: sortColumn === 'lastFlightDate' ? '#0d6efd' : 'inherit' }}
              onClick={() => handleSort('lastFlightDate')}
            >
              Last Flight Date <span style={{ opacity: sortColumn === 'lastFlightDate' ? 1 : 0.4 }}>{sortColumn === 'lastFlightDate' ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅'}</span>
            </th>
            <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', verticalAlign: 'top' }}>Google Calendar Synced</th>
            <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', verticalAlign: 'top' }}>
              <div>Status</div>
              <select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ width: '100%', padding: '0.25rem', marginTop: '0.25rem', fontSize: '0.8rem', border: '1px solid #ddd', borderRadius: '3px' }}
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
                <option value="limited">Limited</option>
                <option value="unverified">Unverified</option>
                <option value="verified">Verified</option>
              </select>
            </th>
            <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', verticalAlign: 'top' }}>
              <div>Role</div>
              <select 
                value={roleFilter} 
                onChange={(e) => setRoleFilter(e.target.value)}
                style={{ width: '100%', padding: '0.25rem', marginTop: '0.25rem', fontSize: '0.8rem', border: '1px solid #ddd', borderRadius: '3px' }}
              >
                <option value="">All</option>
                {(() => {
                  if (roles && !roles.error) {
                    let rolesList = [];
                    if (Array.isArray(roles)) {
                      rolesList = roles;
                    } else if (roles && typeof roles === 'object') {
                      rolesList = Object.values(roles);
                    }
                    return rolesList.map(role => (
                      <option key={role.id} value={role.id}>{role.name}</option>
                    ));
                  }
                  return null;
                })()}
              </select>
            </th>
            <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', verticalAlign: 'top' }}>Aircraft</th>
          </tr>
        </thead>
        <tbody>
          {(Array.isArray(people) ? people : []).filter(person => {
            const firstName = (person.firstName || person.first_name || '').toLowerCase();
            const lastName = (person.lastName || person.last_name || '').toLowerCase();
            const status = (person.status || '').toLowerCase();
            const firstNameMatch = firstName.includes(firstNameFilter.toLowerCase());
            const lastNameMatch = lastName.includes(lastNameFilter.toLowerCase());
            const statusMatch = statusFilter === '' || status === statusFilter.toLowerCase();
            const roleMatch = roleFilter === '' || person.roleId === parseInt(roleFilter);
            return firstNameMatch && lastNameMatch && statusMatch && roleMatch;
          }).slice().sort((a, b) => {
            if (!sortColumn) {
              const aId = Number(a?.id);
              const bId = Number(b?.id);
              const aValue = Number.isFinite(aId) ? aId : Number.MAX_SAFE_INTEGER;
              const bValue = Number.isFinite(bId) ? bId : Number.MAX_SAFE_INTEGER;
              return bValue - aValue;
            }
            const getSortValue = (person) => {
              if (sortColumn === 'createdDate') {
                return person.createdDatetime || person.createdAt || person.createdTimestamp || person.created || person.created_at;
              }
              if (sortColumn === 'lastLogin') {
                return person.lastLogin || person.lastLoginDatetime || person.lastLoginAt || person.last_login;
              }
              if (sortColumn === 'lastFlightDate') {
                return person.lastFlightDate;
              }
              return null;
            };
            const aRaw = getSortValue(a);
            const bRaw = getSortValue(b);
            const aTime = aRaw ? new Date(aRaw).getTime() : NaN;
            const bTime = bRaw ? new Date(bRaw).getTime() : NaN;
            const aValue = Number.isFinite(aTime) ? aTime : -Infinity;
            const bValue = Number.isFinite(bTime) ? bTime : -Infinity;
            return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
          }).map((person, index) => (
            <tr key={person.id || index} 
                style={{ 
                  backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.parentElement.style.backgroundColor = '#e3f2fd'}
                onMouseLeave={(e) => e.target.parentElement.style.backgroundColor = index % 2 === 0 ? 'white' : '#f8f9fa'}>
              <td style={{ ...getFrozenColumnStyle(0, false, index), padding: '0.5rem', border: '1px solid #ccc', textAlign: 'center', fontWeight: 600 }}>
                {index + 1}
              </td>
              <td style={{ ...getFrozenColumnStyle(1, false, index), padding: '0.5rem', border: '1px solid #ccc', textAlign: 'center' }}>
                <button
                  onClick={() => handleDeletePerson(person)}
                  disabled={!canDeletePeople}
                  title={canDeletePeople ? 'Delete person' : 'Only administrators and superusers can delete people'}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: canDeletePeople ? 'pointer' : 'not-allowed',
                    fontSize: '16px',
                    opacity: canDeletePeople ? 1 : 0.4
                  }}
                >
                  🗑️
                </button>
              </td>
              <td style={{ ...getFrozenColumnStyle(2, false, index), padding: '0.35rem', border: '1px solid #ccc', textAlign: 'center', cursor: 'pointer' }} onClick={() => handleEdit(person)}>
                {person.profileImageUrl ? (
                  <img
                    src={person.profileImageUrl}
                    alt={`${person.firstName || person.username || 'User'}`}
                    onMouseEnter={(e) => setHoveredProfileImage({ url: person.profileImageUrl, x: e.clientX, y: e.clientY })}
                    onMouseMove={(e) => setHoveredProfileImage({ url: person.profileImageUrl, x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => setHoveredProfileImage(null)}
                    style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover', display: 'block', margin: '0 auto' }}
                  />
                ) : ''}
                {hoveredProfileImage?.url === person.profileImageUrl && person.profileImageUrl && (
                  <img
                    src={hoveredProfileImage.url}
                    alt=""
                    style={{
                      position: 'fixed',
                      left: Math.min(hoveredProfileImage.x + 16, window.innerWidth - 236),
                      top: Math.min(Math.max(hoveredProfileImage.y - 110, 8), window.innerHeight - 228),
                      width: '220px',
                      height: '220px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: '2px solid white',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
                      pointerEvents: 'none',
                      zIndex: 3000
                    }}
                  />
                )}
              </td>
              <td style={{ ...getFrozenColumnStyle(3, false, index), padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer' }} onClick={() => handleEdit(person)}>{person.firstName || person.first_name || ''}</td>
              <td style={{ ...getFrozenColumnStyle(4, false, index), padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer' }} onClick={() => handleEdit(person)}>{person.lastName || person.last_name || ''}</td>
              <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer' }} onClick={() => handleEdit(person)}>{person.email || ''}</td>
              <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer' }} onClick={() => handleEdit(person)}>{person.phone || ''}</td>
              <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer' }} onClick={() => handleEdit(person)}>{person.address1 || ''}</td>
              <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer' }} onClick={() => handleEdit(person)}>{person.city || ''}</td>
              <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer' }} onClick={() => handleEdit(person)}>{person.state || ''}</td>
              <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer' }} onClick={() => handleEdit(person)}>{person.zip || ''}</td>
              <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer' }} onClick={() => handleEdit(person)}>
                {formatDateOnly(person.createdDatetime || person.createdAt || person.createdTimestamp || person.created || person.created_at)}
              </td>
              <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer' }} onClick={() => handleEdit(person)}>
                {formatDateOnly(person.lastLogin || person.lastLoginDatetime || person.lastLoginAt || person.last_login)}
              </td>
              <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer' }} onClick={() => handleEdit(person)}>
                {formatDateOnly(person.lastFlightDate)}
              </td>
              <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer' }} onClick={() => handleEdit(person)}>
                {person.isGoogleCalSynced === true ? 'Yes' : 'No'}
              </td>
              <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer' }} onClick={() => handleEdit(person)}>{person.status || ''}</td>
              <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer' }} onClick={() => handleEdit(person)}>
                {(() => {
                  if (roles && roles.error) {
                    // Fallback: show role based on roleId pattern
                    if (person.roleId === 3) return 'Instructor';
                    if (person.roleId === 4) return 'Student';
                    if (person.roleId === 5) return 'Admin';
                    return `Role ${person.roleId}`;
                  }
                  let rolesList = [];
                  if (Array.isArray(roles)) {
                    rolesList = roles;
                  } else if (roles && typeof roles === 'object') {
                    rolesList = Object.values(roles);
                  }
                  return rolesList.find(r => r.id === person.roleId)?.name || '';
                })()}
              </td>
              <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer' }} onClick={() => handleEdit(person)}>
                {person.aircraftList && person.aircraftList.length > 0 ? (
                  person.aircraftList.map((ac, index) => {
                    const plane = aircraft.find(a => a.id == ac.aircraftId);
                    if (plane) {
                      return `${plane.model || 'Unknown'} - ${plane.tailNumber || plane.registration || 'Unknown'}`;
                    }
                    return null;
                  }).filter(Boolean).join(', ')
                ) : ''}
              </td>
            </tr>
          ))}
        </tbody>
        </table>
      </div>

      <UserDialog 
        showDialog={showForm}
        onClose={handleCancel}
        userData={showForm ? (dialogUserData || (editingId ? people.find(p => Number(p.id) === Number(editingId)) : null)) : null}
        isReadOnly={false}
        parentMessage={message}
        onSave={(data) => {
          const mockEvent = { 
            preventDefault: () => {},
            formData: data
          };
          return handleSubmit(mockEvent);
        }}
      />









      {showDeleteModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '400px' }}>
            <h3 style={{ margin: '0 0 1rem 0', textAlign: 'center' }}>Confirm Delete</h3>
            <p style={{ margin: '0 0 2rem 0', textAlign: 'center' }}>Are you sure you want to delete the user {deletePersonData?.displayName}?</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button onClick={confirmDelete} style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}>Yes, Delete</button>
              <button onClick={() => setShowDeleteModal(false)} style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showReceiptModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Receipt Image</h3>
              <button onClick={() => setShowReceiptModal(false)} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', padding: '0.25rem' }}>✕</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', textAlign: 'center' }}>
              <img src={selectedReceiptImage} alt="Receipt" style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
            </div>
          </div>
        </div>
      )}

      {showDeleteWorkLessonModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '400px' }}>
            <h3 style={{ margin: '0 0 1rem 0', textAlign: 'center' }}>Confirm Delete</h3>
            <p style={{ margin: '0 0 2rem 0', textAlign: 'center' }}>Are you sure you want to delete this work lesson?</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button onClick={() => { deleteWorkLesson(deleteWorkLessonId); setShowDeleteWorkLessonModal(false); setDeleteWorkLessonId(null); }} style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}>Yes</button>
              <button onClick={() => { setShowDeleteWorkLessonModal(false); setDeleteWorkLessonId(null); }} style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}>No</button>
            </div>
          </div>
        </div>
      )}

      {showEarningsDialog && selectedEarnings && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '90vw', maxHeight: '72vh', display: 'flex', flexDirection: 'column', width: '800px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Earnings Statement Details</h3>
              <button onClick={() => setShowEarningsDialog(false)} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', padding: '0.25rem' }}>✕</button>
            </div>
            <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#e3f2fd', borderRadius: '4px', border: '2px solid #2196f3' }}>
              <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                <strong>Period: {selectedEarnings.startDate ? new Date(selectedEarnings.startDate + 'T00:00:00').toLocaleDateString() : ''} - {selectedEarnings.endDate ? new Date(selectedEarnings.endDate + 'T00:00:00').toLocaleDateString() : ''}</strong>
              </div>
              <div style={{ fontSize: '1rem' }}>
                <strong>Available Rental Hours: {people.find(p => p.id === editingId)?.creditRentalHours || 0}</strong>
                <br />
                <strong>Rental Hours Earned: {selectedEarnings.rentalHoursEarned || 0}</strong>
                <br />
                <strong>Rental Hours Used: {selectedEarnings.rentalHoursUsed || 0}</strong>
              </div>
            </div>
            <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <strong>Pay period Earnings: ${earningsWorkLessons.reduce((total, lesson) => total + (((parseFloat(lesson.dualGiven) || 0) + (parseFloat(lesson.groundInstruction) || 0)) * (people.find(p => p.id === editingId)?.rate || 0)), 0).toFixed(2)}</strong>
              <br />
              <strong>Pay period Expenses: ${earningsExpenses.reduce((total, expense) => total + (parseFloat(expense.total) || 0), 0).toFixed(2)}</strong>
            </div>
            <div style={{ display: 'flex', backgroundColor: '#e9ecef', paddingLeft: '1rem', paddingRight: '1rem', paddingTop: '1rem' }}>
              <button type="button" onClick={() => setEarningsDialogTab('lessons')} style={{ 
                padding: '0.5rem 0.75rem', 
                backgroundColor: earningsDialogTab === 'lessons' ? '#f0f8f0' : '#f0f8f0',
                color: '#495057',
                border: '1px solid #ccc', 
                borderBottom: earningsDialogTab === 'lessons' ? '1px solid #f0f8f0' : '1px solid #ccc',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                cursor: 'pointer',
                fontWeight: earningsDialogTab === 'lessons' ? 'bold' : 'normal',
                marginRight: '2px',
                fontSize: '0.8rem'
              }}>Work Lessons ({earningsWorkLessons.length})</button>
              <button type="button" onClick={() => setEarningsDialogTab('expenses')} style={{ 
                padding: '0.5rem 0.75rem', 
                backgroundColor: earningsDialogTab === 'expenses' ? '#fff0e6' : '#fff0e6',
                color: '#495057',
                border: '1px solid #ccc', 
                borderBottom: earningsDialogTab === 'expenses' ? '1px solid #fff0e6' : '1px solid #ccc',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                cursor: 'pointer',
                fontWeight: earningsDialogTab === 'expenses' ? 'bold' : 'normal',
                fontSize: '0.8rem'
              }}>Expenses ({earningsExpenses.length})</button>
            </div>
            <div style={{ height: '320px', overflow: 'auto', backgroundColor: earningsDialogTab === 'lessons' ? '#f0f8f0' : '#fff0e6', border: '1px solid #ccc', borderTop: 'none', padding: '1rem' }}>
              {earningsDialogTab === 'lessons' && (
                <div style={{ height: '100%', overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', backgroundColor: 'white' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Date</th>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Client</th>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Dual Given</th>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Ground</th>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Total</th>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Earned</th>
                      </tr>
                    </thead>
                    <tbody>
                      {earningsWorkLessons.map((lesson, index) => (
                        <tr key={index}>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{lesson.date ? new Date(lesson.date + 'T00:00:00').toLocaleDateString() : ''}</td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{getClientName(lesson.clientId, lesson.clientName)}</td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{lesson.dualGiven || 0}</td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{lesson.groundInstruction || 0}</td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{lesson.total || 0}</td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>${(((parseFloat(lesson.dualGiven) || 0) + (parseFloat(lesson.groundInstruction) || 0)) * (people.find(p => p.id === editingId)?.rate || 0)).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {earningsDialogTab === 'expenses' && (
                <div style={{ height: '100%', overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', backgroundColor: 'white' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Date</th>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Expense</th>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Vendor</th>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {earningsExpenses.map((expense, index) => (
                        <tr key={index}>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{expense.date ? new Date(expense.date + 'T00:00:00').toLocaleDateString() : ''}</td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{expense.expense || ''}</td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{expense.vendor || ''}</td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>${expense.total || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Assign Person Dialog */}
      {showAssignDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: '500px', maxHeight: '80vh', overflow: 'auto' }}>
            <h3 style={{ margin: '0 0 1rem 0', textAlign: 'center' }}>Assign Person to Organization</h3>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Search by Last Name or Email:</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input 
                  type="text" 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Enter last name or email..."
                  style={{ flex: 1, padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                />
                <button 
                  onClick={searchUsers}
                  style={{ backgroundColor: '#007bff', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}
                >
                  Search
                </button>
              </div>
            </div>
            
            {searchResults.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Select User:</label>
                <div style={{ maxHeight: '150px', overflow: 'auto', border: '1px solid #ccc', borderRadius: '4px' }}>
                  {searchResults.map(user => (
                    <div 
                      key={user.id}
                      onClick={() => setSelectedUser(user)}
                      style={{ 
                        padding: '0.5rem', 
                        cursor: 'pointer', 
                        backgroundColor: selectedUser?.id === user.id ? '#e3f2fd' : 'white',
                        borderBottom: '1px solid #eee'
                      }}
                    >
                      <strong>{user.firstName} {user.lastName}</strong> - {user.email}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {selectedUser && (
              <>
                <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: '#f0f8ff', borderRadius: '4px' }}>
                  <strong>Selected: {selectedUser.firstName} {selectedUser.lastName}</strong> ({selectedUser.email})
                </div>
                
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Role:</label>
                  <select 
                    value={assignRole}
                    onChange={(e) => setAssignRole(e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                  >
                    <option value="">Select Role</option>
                    {Array.isArray(roles) ? roles.filter(role => role.name !== 'admin' && role.name !== 'superuser').map(role => (
                      <option key={role.id} value={role.id}>{role.name}</option>
                    )) : roles && typeof roles === 'object' ? Object.values(roles).filter(role => role.name !== 'admin' && role.name !== 'superuser').map(role => (
                      <option key={role.id} value={role.id}>{role.name}</option>
                    )) : null}
                  </select>
                </div>
                
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Organization Role:</label>
                  <select 
                    value={assignOrgRole}
                    onChange={(e) => setAssignOrgRole(e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                  >
                    <option value="">Select Organization Role</option>
                    <option value="client">Client</option>
                    <option value="member">Member</option>
                    <option value="staff">Staff</option>
                    {(currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') && (
                      <option value="admin">Admin</option>
                    )}
                  </select>
                </div>
                
                {(() => {
                  if (roles && roles.error) {
                    return assignRole === '3';
                  }
                  let rolesList = [];
                  if (Array.isArray(roles)) {
                    rolesList = roles;
                  } else if (roles && typeof roles === 'object') {
                    rolesList = Object.values(roles);
                  }
                  const selectedRole = rolesList.find(r => r.id === parseInt(assignRole));
                  return selectedRole?.name === 'instructor';
                })() && (currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') && (
                  <>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Student Rate ($/hour):</label>
                      <input 
                        type="number" 
                        step="0.01" 
                        value={assignRate}
                        onChange={(e) => setAssignRate(e.target.value)}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                      />
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Instructor Pay Rate ($/hour):</label>
                      <input 
                        type="number" 
                        step="0.01" 
                        value={assignPayRate}
                        onChange={(e) => setAssignPayRate(e.target.value)}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                      />
                    </div>
                  </>
                )}
              </>
            )}
            
            {assignMessage && (
              <div style={{ 
                padding: '0.75rem', 
                marginBottom: '1rem', 
                borderRadius: '4px', 
                backgroundColor: assignMessage.includes('Error') ? '#f8d7da' : '#d4edda',
                color: assignMessage.includes('Error') ? '#721c24' : '#155724',
                border: `1px solid ${assignMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}`
              }}>
                {assignMessage}
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button 
                onClick={assignUserToOrganization}
                disabled={!selectedUser || !assignRole || !assignOrgRole}
                style={{ 
                  backgroundColor: (!selectedUser || !assignRole || !assignOrgRole) ? '#ccc' : '#28a745', 
                  color: 'white', 
                  border: 'none', 
                  padding: '0.5rem 1rem', 
                  borderRadius: '4px', 
                  cursor: (!selectedUser || !assignRole || !assignOrgRole) ? 'not-allowed' : 'pointer' 
                }}
              >
                Assign to Organization
              </button>
              <button 
                onClick={() => {
                  setShowAssignDialog(false);
                  setSelectedUser(null);
                  setAssignRole('');
                  setAssignOrgRole('');
                  setAssignRate('');
                  setAssignPayRate('');
                  setSearchQuery('');
                  setSearchResults([]);
                  setAssignMessage('');
                }}
                style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      <ConfirmDialog 
        show={showConfirmDialog}
        title={confirmDialogData.title}
        message={confirmDialogData.message}
        onConfirm={confirmDialogData.onConfirm}
        onCancel={() => setShowConfirmDialog(false)}
        confirmText="Yes"
        cancelText="No"
      />
      </div>
    </div>
  );
};

export default People;